import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { checkAdminServer } from '@/hooks/useAdmin'

// Tester email - only this user has access to animation testing page
const TESTER_EMAIL = 'yuvalkrispin16@gmail.com'

// ============================================
// AUTH HELPERS
// ============================================

/**
 * Server-side admin check. No admin identity info lives in the frontend.
 * Delegates to the backend /api/admin/check endpoint.
 */
export async function isAdmin(): Promise<boolean> {
  return checkAdminServer()
}

export async function isTester(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user?.email) return false
  return user.email.toLowerCase() === TESTER_EMAIL.toLowerCase()
}

// React hook for checking tester status in components
export function useTester(): { isTester: boolean; loading: boolean } {
  const [isTesterUser, setIsTesterUser] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    isTester().then((result) => {
      setIsTesterUser(result)
      setLoading(false)
    })
  }, [])

  return { isTester: isTesterUser, loading }
}

/**
 * Server-side admin verification. No admin identity info lives in the frontend.
 * Returns user info only if the server confirms admin status.
 */
export async function verifyAdmin(): Promise<{ isAdmin: boolean; email?: string; userId?: string }> {
  const isAdminUser = await checkAdminServer()
  if (!isAdminUser) return { isAdmin: false }

  const { data: { user } } = await supabase.auth.getUser()
  return { isAdmin: true, email: user?.email, userId: user?.id }
}

// ============================================
// TYPES
// ============================================

export interface AdminStats {
  users: {
    total: number
    newToday: number
    newThisWeek: number
    newThisMonth: number
  }
  habits: {
    total: number
    active: number
    conquered: number
    archived: number
  }
  completions: {
    total: number
    today: number
    gold: number
    silver: number
    quick: number
  }
  engagement: {
    dailyActiveUsers: number
    completionRate: number
    avgStreak: number
  }
  economy: {
    totalShardsEarned: number
    totalShardsSpent: number
    shieldsOwned: number
    restTicketsOwned: number
    shieldsUsed: number
    restTicketsUsed: number
    shieldRevenue: number
    restTicketRevenue: number
    avgShardsPerUser: number
  }
}

export interface GrowthMetrics {
  userGrowthRate: number
  habitGrowthRate: number
  completionGrowthRate: number
  signupsThisWeek: number
  signupsLastWeek: number
  habitsCreatedThisWeek: number
  habitsCreatedLastWeek: number
  conquestsThisWeek: number
  conquestsLastWeek: number
}

export interface TimeSeriesDataPoint {
  date: string
  value: number
  label?: string
}

export interface AdminHistoricalData {
  userSignups: TimeSeriesDataPoint[]
  dailyCompletions: TimeSeriesDataPoint[]
  dailyActiveUsers: TimeSeriesDataPoint[]
  completionsByType: { name: string; value: number }[]
  topHabits: { name: string; completions: number; users: number }[]
  streakDistribution: { range: string; count: number }[]
}

export interface AdminUser {
  id: string
  email: string
  username: string | null
  displayName: string | null
  habitsCount: number
  streak: number
  shards: number
  createdAt: string
  notificationsEnabled: boolean
  subscriptionTier: string
  subscriptionStatus: string | null
  subscriptionProvider: string | null
  subscriptionExpiresAt: string | null
  platform: 'ios' | 'android' | 'web' | null
}

export interface UserHealthMetrics {
  activeToday: number
  activeThisWeek: number
  activeThisMonth: number
  churned: number
  powerUsers: number
  newUsersToday: number
  totalUsers: number
  wauMauRatio: number
  dauWauRatio: number
}

export interface ContentMetrics {
  totalPhotosUploaded: number
  photosToday: number
  photosThisWeek: number
  aiApprovalRate: number
  goldRate: number
  silverRate: number
  rejectionRate: number
  averagePhotosPerUser: number
  usersWithPhotos: number
  quickCompleteRate: number
}

export interface SocialMetrics {
  totalComments: number
  commentsToday: number
  commentsThisWeek: number
  totalFollows: number
  newFollowsThisWeek: number
  usersWithFollowers: number
  averageFollowersPerUser: number
  publicHabits: number
  privateHabits: number
}

export interface ChallengeOverview {
  totalChallenges: number
  activeChallenges: number
  totalParticipants: number
  overallCompletionRate: number
  mostPopularChallenge: { title: string; participants: number } | null
}

export interface StreakHealth {
  usersAtRiskToday: number
  protectedByShieldsToday: number
  brokenStreaksThisWeek: number
  averageStreakLength: number
  usersWithStreaks: number
}

export interface FeatureUsage {
  photoVerificationUsage: number
  quickCompleteUsage: number
  challengeParticipation: number
  shieldUsage: number
  restDayUsage: number
  profileCustomization: number
  notificationOptIn: number
}

export interface TopUser {
  id: string
  displayName: string
  username: string | null
  streak: number
  shards: number
  completions: number
  habits: number
}

export interface RetentionMetrics {
  day1Retention: number
  day7Retention: number
  day30Retention: number
  retentionTrend: { day: number; rate: number }[]
}

export interface ActivityHeatmap {
  byHour: { hour: number; completions: number }[]
  byDayOfWeek: { day: string; completions: number }[]
}

export interface PurchaseTrend {
  date: string
  shields: number
  restTickets: number
  totalShards: number
}

export interface TopSpender {
  userId: string
  displayName: string
  email: string
  totalSpent: number
  shieldsPurchased: number
  restTicketsPurchased: number
}

export interface TopHabit {
  id: string
  title: string
  emoji: string | null
  ownerName: string
  completions: number
  currentStreak: number
}

export interface BugReport {
  id: string
  user_id: string | null
  title: string
  description: string
  category: string
  severity: string
  user_agent: string | null
  platform: string | null
  browser: string | null
  browser_version: string | null
  screen_width: number | null
  screen_height: number | null
  viewport_width: number | null
  viewport_height: number | null
  device_pixel_ratio: number | null
  is_mobile: boolean
  is_pwa: boolean
  current_url: string | null
  referrer_url: string | null
  app_version: string | null
  user_email: string | null
  username: string | null
  user_streak: number | null
  user_shards: number | null
  habits_count: number | null
  connection_type: string | null
  is_online: boolean
  status: string
  admin_notes: string | null
  resolved_at: string | null
  resolved_by: string | null
  created_at: string
  updated_at: string
}

export interface BugStats {
  total: number
  byStatus: Record<string, number>
  bySeverity: Record<string, number>
  byCategory: Record<string, number>
}

// ============================================
// STATS
// ============================================

export async function getAdminStats(): Promise<AdminStats | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekStart = new Date(todayStart)
  weekStart.setDate(weekStart.getDate() - 7)
  const monthStart = new Date(todayStart)
  monthStart.setDate(monthStart.getDate() - 30)
  
  // Fetch all data in parallel
  const [
    usersResult,
    habitsResult,
    logsResult,
    todayLogsResult,
    inventoryResult,
    profilesResult,
    transactionsResult,
    shieldsUsedResult,
    restDaysResult,
  ] = await Promise.all([
    supabase.from('user_profiles').select('id, created_at'),
    supabase.from('habits').select('id, is_archived, conquered_at'),
    supabase.from('evidence_logs').select('id, ai_verdict, shards_earned, created_at'),
    supabase.from('evidence_logs').select('id, ai_verdict, user_id').gte('created_at', todayStart.toISOString()),
    supabase.from('inventory').select('item_type, quantity'),
    supabase.from('user_profiles').select('id, currency_shards, current_streak_global'),
    supabase.from('shop_transactions').select('item_type, shards_spent'),
    supabase.from('shield_usage').select('id'),
    supabase.from('rest_days').select('id'),
  ])
  
  const users = (usersResult.data || []) as { id: string; created_at: string }[]
  const habits = (habitsResult.data || []) as { id: string; is_archived: boolean; conquered_at: string | null }[]
  const logs = (logsResult.data || []) as { id: string; ai_verdict: string; shards_earned: number; created_at: string }[]
  const todayLogs = (todayLogsResult.data || []) as { id: string; ai_verdict: string; user_id: string }[]
  const inventory = (inventoryResult.data || []) as { item_type: string; quantity: number }[]
  const profiles = (profilesResult.data || []) as { id: string; currency_shards: number; current_streak_global: number }[]
  const transactions = (transactionsResult.data || []) as { item_type: string; shards_spent: number }[]
  const shieldsUsed = (shieldsUsedResult.data || []) as { id: string }[]
  const restDays = (restDaysResult.data || []) as { id: string }[]
  
  // Calculate user stats
  const newToday = users.filter(u => new Date(u.created_at) >= todayStart).length
  const newThisWeek = users.filter(u => new Date(u.created_at) >= weekStart).length
  const newThisMonth = users.filter(u => new Date(u.created_at) >= monthStart).length
  
  // Calculate habit stats
  const activeHabits = habits.filter(h => !h.is_archived && !h.conquered_at).length
  const conqueredHabits = habits.filter(h => h.conquered_at).length
  const archivedHabits = habits.filter(h => h.is_archived).length
  
  // Calculate completion stats
  const goldLogs = logs.filter(l => l.ai_verdict === 'approved_gold').length
  const silverLogs = logs.filter(l => l.ai_verdict === 'approved_silver').length
  const quickLogs = logs.filter(l => l.ai_verdict === 'quick_complete').length
  const successfulLogs = goldLogs + silverLogs + quickLogs
  
  const todaySuccessful = todayLogs.filter(l => 
    l.ai_verdict === 'approved_gold' || 
    l.ai_verdict === 'approved_silver' || 
    l.ai_verdict === 'quick_complete'
  )
  
  // Daily active users (users with completions today)
  const uniqueUsersToday = new Set(todaySuccessful.map(l => l.user_id)).size
  
  // Completion rate (successful / total attempts)
  const completionRate = logs.length > 0 ? (successfulLogs / logs.length) * 100 : 0
  
  // Average streak
  const avgStreak = profiles.length > 0 
    ? profiles.reduce((sum, p) => sum + (p.current_streak_global || 0), 0) / profiles.length 
    : 0
  
  // Economy stats
  const totalShardsEarned = logs.reduce((sum, l) => sum + (l.shards_earned || 0), 0)
  const totalShardsSpent = totalShardsEarned - profiles.reduce((sum, p) => sum + (p.currency_shards || 0), 0)
  const shieldsOwned = inventory.filter(i => i.item_type === 'streak_shield').reduce((sum, i) => sum + i.quantity, 0)
  const restTicketsOwned = inventory.filter(i => i.item_type === 'rest_ticket').reduce((sum, i) => sum + i.quantity, 0)
  
  // Additional economy stats from transactions
  const shieldTransactions = transactions.filter(t => t.item_type === 'streak_shield')
  const restTicketTransactions = transactions.filter(t => t.item_type === 'rest_ticket')
  const shieldRevenue = shieldTransactions.reduce((sum, t) => sum + (t.shards_spent || 0), 0)
  const restTicketRevenue = restTicketTransactions.reduce((sum, t) => sum + (t.shards_spent || 0), 0)
  const avgShardsPerUser = profiles.length > 0 
    ? Math.round(profiles.reduce((sum, p) => sum + (p.currency_shards || 0), 0) / profiles.length)
    : 0
  
  return {
    users: {
      total: users.length,
      newToday,
      newThisWeek,
      newThisMonth,
    },
    habits: {
      total: habits.length,
      active: activeHabits,
      conquered: conqueredHabits,
      archived: archivedHabits,
    },
    completions: {
      total: successfulLogs,
      today: todaySuccessful.length,
      gold: goldLogs,
      silver: silverLogs,
      quick: quickLogs,
    },
    engagement: {
      dailyActiveUsers: uniqueUsersToday,
      completionRate: Math.round(completionRate * 10) / 10,
      avgStreak: Math.round(avgStreak * 10) / 10,
    },
    economy: {
      totalShardsEarned,
      totalShardsSpent: Math.max(0, totalShardsSpent),
      shieldsOwned,
      restTicketsOwned,
      shieldsUsed: shieldsUsed.length,
      restTicketsUsed: restDays.length,
      shieldRevenue,
      restTicketRevenue,
      avgShardsPerUser,
    },
  }
}

// ============================================
// GROWTH METRICS
// ============================================

export async function getGrowthMetrics(): Promise<GrowthMetrics | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const thisWeekStart = new Date(now)
  thisWeekStart.setDate(thisWeekStart.getDate() - 7)
  const lastWeekStart = new Date(thisWeekStart)
  lastWeekStart.setDate(lastWeekStart.getDate() - 7)
  
  const [usersResult, habitsResult, logsResult] = await Promise.all([
    supabase.from('user_profiles').select('id, created_at'),
    supabase.from('habits').select('id, created_at, conquered_at'),
    supabase.from('evidence_logs').select('id, created_at')
      .gte('created_at', lastWeekStart.toISOString())
      .in('ai_verdict', ['approved_gold', 'approved_silver', 'quick_complete']),
  ])
  
  const users = (usersResult.data || []) as { id: string; created_at: string }[]
  const habits = (habitsResult.data || []) as { id: string; created_at: string; conquered_at: string | null }[]
  const logs = (logsResult.data || []) as { id: string; created_at: string }[]
  
  const signupsThisWeek = users.filter(u => new Date(u.created_at) >= thisWeekStart).length
  const signupsLastWeek = users.filter(u => {
    const date = new Date(u.created_at)
    return date >= lastWeekStart && date < thisWeekStart
  }).length
  
  const habitsThisWeek = habits.filter(h => new Date(h.created_at) >= thisWeekStart).length
  const habitsLastWeek = habits.filter(h => {
    const date = new Date(h.created_at)
    return date >= lastWeekStart && date < thisWeekStart
  }).length
  
  const completionsThisWeek = logs.filter(l => new Date(l.created_at) >= thisWeekStart).length
  const completionsLastWeek = logs.filter(l => {
    const date = new Date(l.created_at)
    return date >= lastWeekStart && date < thisWeekStart
  }).length
  
  const conquestsThisWeek = habits.filter(h => h.conquered_at && new Date(h.conquered_at) >= thisWeekStart).length
  const conquestsLastWeek = habits.filter(h => {
    if (!h.conquered_at) return false
    const date = new Date(h.conquered_at)
    return date >= lastWeekStart && date < thisWeekStart
  }).length
  
  const calcGrowthRate = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return Math.round(((current - previous) / previous) * 1000) / 10
  }
  
  return {
    userGrowthRate: calcGrowthRate(signupsThisWeek, signupsLastWeek),
    habitGrowthRate: calcGrowthRate(habitsThisWeek, habitsLastWeek),
    completionGrowthRate: calcGrowthRate(completionsThisWeek, completionsLastWeek),
    signupsThisWeek,
    signupsLastWeek,
    habitsCreatedThisWeek: habitsThisWeek,
    habitsCreatedLastWeek: habitsLastWeek,
    conquestsThisWeek,
    conquestsLastWeek,
  }
}

// ============================================
// HISTORICAL DATA FOR CHARTS
// ============================================

export async function getAdminHistoricalData(days: number = 30): Promise<AdminHistoricalData | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const startDate = new Date(now)
  startDate.setDate(startDate.getDate() - days)
  startDate.setHours(0, 0, 0, 0)
  
  // Fetch all data in parallel
  const [usersResult, logsResult, habitsResult, profilesResult] = await Promise.all([
    supabase.from('user_profiles').select('id, created_at'),
    supabase.from('evidence_logs').select('id, ai_verdict, user_id, habit_id, created_at').gte('created_at', startDate.toISOString()),
    supabase.from('habits').select('id, title, user_id'),
    supabase.from('user_profiles').select('id, current_streak_global'),
  ])
  
  const users = (usersResult.data || []) as { id: string; created_at: string }[]
  const logs = (logsResult.data || []) as { id: string; ai_verdict: string; user_id: string; habit_id: string; created_at: string }[]
  const habits = (habitsResult.data || []) as { id: string; title: string; user_id: string }[]
  const profiles = (profilesResult.data || []) as { id: string; current_streak_global: number }[]
  
  // Generate date labels for the past N days
  const dateLabels: string[] = []
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now)
    d.setDate(d.getDate() - i)
    dateLabels.push(d.toISOString().split('T')[0])
  }
  
  // 1. User signups per day
  const signupsByDate = new Map<string, number>()
  dateLabels.forEach(date => signupsByDate.set(date, 0))
  users.forEach(u => {
    const date = new Date(u.created_at).toISOString().split('T')[0]
    if (signupsByDate.has(date)) {
      signupsByDate.set(date, (signupsByDate.get(date) || 0) + 1)
    }
  })
  
  const userSignups: TimeSeriesDataPoint[] = dateLabels.map(date => ({
    date,
    value: signupsByDate.get(date) || 0,
    label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }))
  
  // 2. Daily completions (successful only)
  const successfulLogs = logs.filter(l => 
    l.ai_verdict === 'approved_gold' || 
    l.ai_verdict === 'approved_silver' || 
    l.ai_verdict === 'quick_complete'
  )
  
  const completionsByDate = new Map<string, number>()
  dateLabels.forEach(date => completionsByDate.set(date, 0))
  successfulLogs.forEach(l => {
    const date = new Date(l.created_at).toISOString().split('T')[0]
    if (completionsByDate.has(date)) {
      completionsByDate.set(date, (completionsByDate.get(date) || 0) + 1)
    }
  })
  
  const dailyCompletions: TimeSeriesDataPoint[] = dateLabels.map(date => ({
    date,
    value: completionsByDate.get(date) || 0,
    label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }))
  
  // 3. Daily active users (unique users with completions per day)
  const dauByDate = new Map<string, Set<string>>()
  dateLabels.forEach(date => dauByDate.set(date, new Set()))
  successfulLogs.forEach(l => {
    const date = new Date(l.created_at).toISOString().split('T')[0]
    if (dauByDate.has(date)) {
      dauByDate.get(date)!.add(l.user_id)
    }
  })
  
  const dailyActiveUsers: TimeSeriesDataPoint[] = dateLabels.map(date => ({
    date,
    value: dauByDate.get(date)?.size || 0,
    label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }))
  
  // 4. Completions by type (pie chart data)
  const goldCount = logs.filter(l => l.ai_verdict === 'approved_gold').length
  const silverCount = logs.filter(l => l.ai_verdict === 'approved_silver').length
  const quickCount = logs.filter(l => l.ai_verdict === 'quick_complete').length
  const rejectedCount = logs.filter(l => l.ai_verdict === 'rejected').length
  
  const completionsByType = [
    { name: 'Gold', value: goldCount },
    { name: 'Silver', value: silverCount },
    { name: 'Quick', value: quickCount },
    { name: 'Rejected', value: rejectedCount },
  ].filter(item => item.value > 0)
  
  // 5. Top habits by completions
  const habitCompletions = new Map<string, { completions: number; users: Set<string> }>()
  successfulLogs.forEach(l => {
    if (!habitCompletions.has(l.habit_id)) {
      habitCompletions.set(l.habit_id, { completions: 0, users: new Set() })
    }
    const data = habitCompletions.get(l.habit_id)!
    data.completions++
    data.users.add(l.user_id)
  })
  
  const habitMap = new Map(habits.map(h => [h.id, h.title]))
  const topHabits = Array.from(habitCompletions.entries())
    .map(([id, data]) => ({
      name: habitMap.get(id) || 'Unknown',
      completions: data.completions,
      users: data.users.size,
    }))
    .sort((a, b) => b.completions - a.completions)
    .slice(0, 10)
  
  // 6. Streak distribution
  const streakRanges = [
    { min: 0, max: 0, label: '0 days' },
    { min: 1, max: 7, label: '1-7 days' },
    { min: 8, max: 30, label: '8-30 days' },
    { min: 31, max: 90, label: '31-90 days' },
    { min: 91, max: 365, label: '91-365 days' },
    { min: 366, max: Infinity, label: '365+ days' },
  ]
  
  const streakDistribution = streakRanges.map(range => {
    const count = profiles.filter(p => 
      p.current_streak_global >= range.min && p.current_streak_global <= range.max
    ).length
    return { range: range.label, count }
  })
  
  return {
    userSignups,
    dailyCompletions,
    dailyActiveUsers,
    completionsByType,
    topHabits,
    streakDistribution,
  }
}

// ============================================
// USER HEALTH METRICS
// ============================================

export async function getUserHealthMetrics(): Promise<UserHealthMetrics | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekAgo = new Date(todayStart)
  weekAgo.setDate(weekAgo.getDate() - 7)
  const monthAgo = new Date(todayStart)
  monthAgo.setDate(monthAgo.getDate() - 30)
  
  const [profilesResult, logsResult] = await Promise.all([
    supabase.from('user_profiles').select('id, created_at, current_streak_global'),
    supabase.from('evidence_logs').select('user_id, created_at')
      .gte('created_at', monthAgo.toISOString())
      .in('ai_verdict', ['approved_gold', 'approved_silver', 'quick_complete']),
  ])
  
  const profiles = (profilesResult.data || []) as { id: string; created_at: string; current_streak_global: number }[]
  const logs = (logsResult.data || []) as { user_id: string; created_at: string }[]
  
  // Group logs by user and recency
  const todayUsers = new Set<string>()
  const weekUsers = new Set<string>()
  const monthUsers = new Set<string>()
  
  logs.forEach(log => {
    const logDate = new Date(log.created_at)
    if (logDate >= todayStart) todayUsers.add(log.user_id)
    if (logDate >= weekAgo) weekUsers.add(log.user_id)
    if (logDate >= monthAgo) monthUsers.add(log.user_id)
  })
  
  const churned = profiles.filter(p => !monthUsers.has(p.id) && new Date(p.created_at) < monthAgo).length
  const powerUsers = profiles.filter(p => p.current_streak_global >= 7).length
  const newUsersToday = profiles.filter(p => new Date(p.created_at) >= todayStart).length
  
  const dau = todayUsers.size
  const wau = weekUsers.size
  const mau = monthUsers.size
  
  return {
    activeToday: dau,
    activeThisWeek: wau,
    activeThisMonth: mau,
    churned,
    powerUsers,
    newUsersToday,
    totalUsers: profiles.length,
    wauMauRatio: mau > 0 ? Math.round((wau / mau) * 1000) / 10 : 0,
    dauWauRatio: wau > 0 ? Math.round((dau / wau) * 1000) / 10 : 0,
  }
}

// ============================================
// TOP USERS
// ============================================

export async function getTopUsers(): Promise<{ byStreak: TopUser[]; byCompletions: TopUser[]; byShards: TopUser[] } | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const [profilesResult, habitsResult, logsResult] = await Promise.all([
    supabase.from('user_profiles').select('id, display_name, username, current_streak_global, currency_shards'),
    supabase.from('habits').select('user_id'),
    supabase.from('evidence_logs').select('user_id').in('ai_verdict', ['approved_gold', 'approved_silver', 'quick_complete']),
  ])
  
  const profiles = (profilesResult.data || []) as { id: string; display_name: string | null; username: string | null; current_streak_global: number; currency_shards: number }[]
  const habits = (habitsResult.data || []) as { user_id: string }[]
  const logs = (logsResult.data || []) as { user_id: string }[]
  
  // Count habits and completions per user
  const habitCounts = new Map<string, number>()
  const completionCounts = new Map<string, number>()
  
  habits.forEach(h => habitCounts.set(h.user_id, (habitCounts.get(h.user_id) || 0) + 1))
  logs.forEach(l => completionCounts.set(l.user_id, (completionCounts.get(l.user_id) || 0) + 1))
  
  const enrichedUsers: TopUser[] = profiles.map(p => ({
    id: p.id,
    displayName: p.display_name || 'Unknown',
    username: p.username,
    streak: p.current_streak_global || 0,
    shards: p.currency_shards || 0,
    completions: completionCounts.get(p.id) || 0,
    habits: habitCounts.get(p.id) || 0,
  }))
  
  return {
    byStreak: [...enrichedUsers].sort((a, b) => b.streak - a.streak).slice(0, 10),
    byCompletions: [...enrichedUsers].sort((a, b) => b.completions - a.completions).slice(0, 10),
    byShards: [...enrichedUsers].sort((a, b) => b.shards - a.shards).slice(0, 10),
  }
}

// ============================================
// USER LIST
// ============================================

export async function getAdminUserList(search?: string): Promise<AdminUser[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  // Get all profiles
  const { data: profilesData } = await supabase.from('user_profiles').select('*')
  const profiles = (profilesData || []) as any[]
  const profileMap = new Map(profiles.map(p => [p.id, p]))
  
  // Get habit counts per user
  const { data: habitsData } = await supabase.from('habits').select('user_id, id')
  const habits = (habitsData || []) as { user_id: string; id: string }[]
  const habitCounts = new Map<string, number>()
  habits.forEach(h => {
    habitCounts.set(h.user_id, (habitCounts.get(h.user_id) || 0) + 1)
  })
  
  let users: AdminUser[] = profiles.map(profile => {
    return {
      id: profile.id,
      email: profile.email || '',
      username: profile.username || null,
      displayName: profile.display_name || null,
      habitsCount: habitCounts.get(profile.id) || 0,
      streak: profile.current_streak_global || 0,
      shards: profile.currency_shards || 0,
      createdAt: profile.created_at,
      notificationsEnabled: profile.notification_email_enabled || false,
      subscriptionTier: profile.subscription_tier || 'free',
      subscriptionStatus: profile.subscription_status || null,
      subscriptionProvider: profile.subscription_provider || null,
      subscriptionExpiresAt: profile.subscription_expires_at || null,
      platform: profile.platform || null,
    }
  })
  
  // Filter by search
  if (search) {
    const searchLower = search.toLowerCase()
    users = users.filter(u => 
      u.email.toLowerCase().includes(searchLower) ||
      u.username?.toLowerCase().includes(searchLower) ||
      u.displayName?.toLowerCase().includes(searchLower)
    )
  }
  
  // Sort by created date (newest first)
  users.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  
  return users
}

// ============================================
// CONTENT METRICS
// ============================================

export async function getContentMetrics(): Promise<ContentMetrics | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekAgo = new Date(todayStart)
  weekAgo.setDate(weekAgo.getDate() - 7)
  
  const { data: logsData } = await supabase
    .from('evidence_logs')
    .select('id, user_id, ai_verdict, image_url, created_at')
  
  const logs = (logsData || []) as { id: string; user_id: string; ai_verdict: string; image_url: string | null; created_at: string }[]
  
  const photosLogs = logs.filter(l => l.image_url)
  const todayPhotos = photosLogs.filter(l => new Date(l.created_at) >= todayStart).length
  const weekPhotos = photosLogs.filter(l => new Date(l.created_at) >= weekAgo).length
  
  const goldCount = logs.filter(l => l.ai_verdict === 'approved_gold').length
  const silverCount = logs.filter(l => l.ai_verdict === 'approved_silver').length
  const rejectedCount = logs.filter(l => l.ai_verdict === 'rejected').length
  const quickCount = logs.filter(l => l.ai_verdict === 'quick_complete').length
  const totalVerified = goldCount + silverCount + rejectedCount
  
  const uniqueUsersWithPhotos = new Set(photosLogs.map(l => l.user_id)).size
  
  return {
    totalPhotosUploaded: photosLogs.length,
    photosToday: todayPhotos,
    photosThisWeek: weekPhotos,
    aiApprovalRate: totalVerified > 0 ? Math.round(((goldCount + silverCount) / totalVerified) * 1000) / 10 : 0,
    goldRate: totalVerified > 0 ? Math.round((goldCount / totalVerified) * 1000) / 10 : 0,
    silverRate: totalVerified > 0 ? Math.round((silverCount / totalVerified) * 1000) / 10 : 0,
    rejectionRate: totalVerified > 0 ? Math.round((rejectedCount / totalVerified) * 1000) / 10 : 0,
    averagePhotosPerUser: uniqueUsersWithPhotos > 0 ? Math.round((photosLogs.length / uniqueUsersWithPhotos) * 10) / 10 : 0,
    usersWithPhotos: uniqueUsersWithPhotos,
    quickCompleteRate: logs.length > 0 ? Math.round((quickCount / logs.length) * 1000) / 10 : 0,
  }
}

// ============================================
// SOCIAL METRICS
// ============================================

export async function getSocialMetrics(): Promise<SocialMetrics | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekAgo = new Date(todayStart)
  weekAgo.setDate(weekAgo.getDate() - 7)
  
  const [commentsResult, followsResult, habitsResult] = await Promise.all([
    supabase.from('milestone_comments').select('id, created_at'),
    supabase.from('follows').select('id, follower_id, following_id, created_at'),
    supabase.from('habits').select('id, is_private'),
  ])
  
  const comments = (commentsResult.data || []) as { id: string; created_at: string }[]
  const follows = (followsResult.data || []) as { id: string; follower_id: string; following_id: string; created_at: string }[]
  const habits = (habitsResult.data || []) as { id: string; is_private: boolean }[]
  
  const commentsToday = comments.filter(c => new Date(c.created_at) >= todayStart).length
  const commentsThisWeek = comments.filter(c => new Date(c.created_at) >= weekAgo).length
  const newFollowsThisWeek = follows.filter(f => new Date(f.created_at) >= weekAgo).length
  
  // Count unique users with followers
  const usersWithFollowers = new Set(follows.map(f => f.following_id)).size
  const avgFollowers = usersWithFollowers > 0 ? Math.round((follows.length / usersWithFollowers) * 10) / 10 : 0
  
  const publicHabits = habits.filter(h => !h.is_private).length
  const privateHabits = habits.filter(h => h.is_private).length
  
  return {
    totalComments: comments.length,
    commentsToday,
    commentsThisWeek,
    totalFollows: follows.length,
    newFollowsThisWeek,
    usersWithFollowers,
    averageFollowersPerUser: avgFollowers,
    publicHabits,
    privateHabits,
  }
}

// ============================================
// CHALLENGE OVERVIEW
// ============================================

export async function getChallengeOverview(): Promise<ChallengeOverview | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const [challengesResult, userChallengesResult] = await Promise.all([
    supabase.from('challenges').select('id, title, is_active'),
    supabase.from('user_challenges').select('id, challenge_id, status'),
  ])
  
  const challengeList = (challengesResult.data || []) as { id: string; title: string; is_active: boolean }[]
  const participations = (userChallengesResult.data || []) as { id: string; challenge_id: string; status: string }[]
  
  const activeChallenges = challengeList.filter(c => c.is_active).length
  const completedParticipations = participations.filter(p => p.status === 'completed')
  
  // Calculate most popular challenge
  const challengeParticipantCounts = new Map<string, number>()
  participations.forEach(p => {
    challengeParticipantCounts.set(p.challenge_id, (challengeParticipantCounts.get(p.challenge_id) || 0) + 1)
  })
  
  let mostPopular: { title: string; participants: number } | null = null
  let maxParticipants = 0
  for (const [challengeId, count] of challengeParticipantCounts.entries()) {
    if (count > maxParticipants) {
      const challenge = challengeList.find(c => c.id === challengeId)
      if (challenge) {
        mostPopular = { title: challenge.title, participants: count }
        maxParticipants = count
      }
    }
  }
  
  const overallCompletionRate = participations.length > 0
    ? Math.round((completedParticipations.length / participations.length) * 1000) / 10
    : 0
  
  return {
    totalChallenges: challengeList.length,
    activeChallenges,
    totalParticipants: participations.length,
    overallCompletionRate,
    mostPopularChallenge: mostPopular,
  }
}

// ============================================
// STREAK HEALTH
// ============================================

export async function getStreakHealth(): Promise<StreakHealth | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekAgo = new Date(todayStart)
  weekAgo.setDate(weekAgo.getDate() - 7)
  
  // Get all users with their streak data
  const { data: profilesData } = await supabase.from('user_profiles').select('id, current_streak_global')
  const profiles = (profilesData || []) as { id: string; current_streak_global: number }[]
  
  // Get today's completions
  const { data: todayLogsData } = await supabase
    .from('evidence_logs')
    .select('user_id')
    .gte('created_at', todayStart.toISOString())
    .in('ai_verdict', ['approved_gold', 'approved_silver', 'quick_complete'])
  
  const todayCompletedUsers = new Set((todayLogsData || []).map((l: any) => l.user_id))
  
  // Get active shields being used today
  const { data: shieldsData } = await supabase
    .from('shield_usage')
    .select('user_id')
    .eq('protected_date', todayStart.toISOString().split('T')[0])
  
  const protectedUsers = new Set((shieldsData || []).map((s: any) => s.user_id))
  
  // Calculate at-risk users (have streak > 7 days, haven't completed today, not protected)
  let usersAtRisk = 0
  let totalStreakLength = 0
  let usersWithStreaks = 0
  
  for (const profile of profiles) {
    if (profile.current_streak_global > 0) {
      usersWithStreaks++
      totalStreakLength += profile.current_streak_global
      
      if (profile.current_streak_global >= 7 && 
          !todayCompletedUsers.has(profile.id) && 
          !protectedUsers.has(profile.id)) {
        usersAtRisk++
      }
    }
  }
  
  // Get broken streaks this week (milestone events)
  const { data: brokenStreaksData } = await supabase
    .from('milestone_events')
    .select('id')
    .eq('milestone_type', 'streak_broken')
    .gte('created_at', weekAgo.toISOString())
  
  return {
    usersAtRiskToday: usersAtRisk,
    protectedByShieldsToday: protectedUsers.size,
    brokenStreaksThisWeek: (brokenStreaksData || []).length,
    averageStreakLength: usersWithStreaks > 0 ? Math.round((totalStreakLength / usersWithStreaks) * 10) / 10 : 0,
    usersWithStreaks,
  }
}

// ============================================
// FEATURE USAGE
// ============================================

export async function getFeatureUsage(): Promise<FeatureUsage | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const weekAgo = new Date(now)
  weekAgo.setDate(weekAgo.getDate() - 7)
  
  const [
    logsResult,
    profilesResult,
    challengeUsersResult,
    shieldsResult,
    restDaysResult,
  ] = await Promise.all([
    supabase.from('evidence_logs').select('id, ai_verdict, image_url'),
    supabase.from('user_profiles').select('id, profile_emoji, notification_email_enabled'),
    supabase.from('user_challenges').select('user_id'),
    supabase.from('shield_usage').select('id').gte('protected_date', weekAgo.toISOString().split('T')[0]),
    supabase.from('rest_days').select('id').gte('date', weekAgo.toISOString().split('T')[0]),
  ])
  
  const logs = (logsResult.data || []) as { id: string; ai_verdict: string; image_url: string | null }[]
  const profiles = (profilesResult.data || []) as { id: string; profile_emoji: string | null; notification_email_enabled: boolean }[]
  const challengeUsers = (challengeUsersResult.data || []) as { user_id: string }[]
  const shields = (shieldsResult.data || []) as { id: string }[]
  const restDays = (restDaysResult.data || []) as { id: string }[]
  
  const successfulLogs = logs.filter(l => ['approved_gold', 'approved_silver', 'quick_complete'].includes(l.ai_verdict))
  const photoLogs = successfulLogs.filter(l => l.image_url)
  const quickLogs = logs.filter(l => l.ai_verdict === 'quick_complete')
  
  const uniqueChallengeUsers = new Set(challengeUsers.map(c => c.user_id)).size
  const usersWithEmoji = profiles.filter(p => p.profile_emoji).length
  const usersWithNotifications = profiles.filter(p => p.notification_email_enabled).length
  
  return {
    photoVerificationUsage: successfulLogs.length > 0 ? Math.round((photoLogs.length / successfulLogs.length) * 1000) / 10 : 0,
    quickCompleteUsage: successfulLogs.length > 0 ? Math.round((quickLogs.length / successfulLogs.length) * 1000) / 10 : 0,
    challengeParticipation: profiles.length > 0 ? Math.round((uniqueChallengeUsers / profiles.length) * 1000) / 10 : 0,
    shieldUsage: shields.length,
    restDayUsage: restDays.length,
    profileCustomization: profiles.length > 0 ? Math.round((usersWithEmoji / profiles.length) * 1000) / 10 : 0,
    notificationOptIn: profiles.length > 0 ? Math.round((usersWithNotifications / profiles.length) * 1000) / 10 : 0,
  }
}

// ============================================
// RETENTION METRICS
// ============================================

export async function getRetentionMetrics(): Promise<RetentionMetrics | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const thirtyDaysAgo = new Date(now)
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  
  // Get users who signed up in the last 30 days
  const { data: usersData } = await supabase
    .from('user_profiles')
    .select('id, created_at')
    .gte('created_at', thirtyDaysAgo.toISOString())
  
  const users = (usersData || []) as { id: string; created_at: string }[]
  
  // Get all evidence logs from the last 30 days
  const { data: logsData } = await supabase
    .from('evidence_logs')
    .select('user_id, created_at')
    .gte('created_at', thirtyDaysAgo.toISOString())
  
  const logs = (logsData || []) as { user_id: string; created_at: string }[]
  
  // Calculate retention rates
  let day1Count = 0, day7Count = 0, day30Count = 0
  let day1Total = 0, day7Total = 0, day30Total = 0
  
  for (const user of users) {
    const signupDate = new Date(user.created_at)
    const daysSinceSignup = Math.floor((now.getTime() - signupDate.getTime()) / (1000 * 60 * 60 * 24))
    
    const userLogs = logs.filter(l => l.user_id === user.id)
    
    // Day 1 retention (returned the next day)
    if (daysSinceSignup >= 1) {
      day1Total++
      const day1Start = new Date(signupDate)
      day1Start.setDate(day1Start.getDate() + 1)
      day1Start.setHours(0, 0, 0, 0)
      const day1End = new Date(day1Start)
      day1End.setDate(day1End.getDate() + 1)
      
      const hasDay1Activity = userLogs.some(l => {
        const logDate = new Date(l.created_at)
        return logDate >= day1Start && logDate < day1End
      })
      if (hasDay1Activity) day1Count++
    }
    
    // Day 7 retention
    if (daysSinceSignup >= 7) {
      day7Total++
      const day7Start = new Date(signupDate)
      day7Start.setDate(day7Start.getDate() + 7)
      const hasDay7Activity = userLogs.some(l => new Date(l.created_at) >= day7Start)
      if (hasDay7Activity) day7Count++
    }
    
    // Day 30 retention
    if (daysSinceSignup >= 30) {
      day30Total++
      const day30Start = new Date(signupDate)
      day30Start.setDate(day30Start.getDate() + 30)
      const hasDay30Activity = userLogs.some(l => new Date(l.created_at) >= day30Start)
      if (hasDay30Activity) day30Count++
    }
  }
  
  const day1Retention = day1Total > 0 ? Math.round((day1Count / day1Total) * 1000) / 10 : 0
  const day7Retention = day7Total > 0 ? Math.round((day7Count / day7Total) * 1000) / 10 : 0
  const day30Retention = day30Total > 0 ? Math.round((day30Count / day30Total) * 1000) / 10 : 0
  
  return {
    day1Retention,
    day7Retention,
    day30Retention,
    retentionTrend: [
      { day: 1, rate: day1Retention },
      { day: 7, rate: day7Retention },
      { day: 30, rate: day30Retention },
    ],
  }
}

// ============================================
// ACTIVITY HEATMAP
// ============================================

export async function getActivityHeatmap(days: number = 30): Promise<ActivityHeatmap | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const startDate = new Date(now)
  startDate.setDate(startDate.getDate() - days)
  
  const { data: logsData } = await supabase
    .from('evidence_logs')
    .select('created_at, ai_verdict')
    .gte('created_at', startDate.toISOString())
    .in('ai_verdict', ['approved_gold', 'approved_silver', 'quick_complete'])
  
  const logs = (logsData || []) as { created_at: string; ai_verdict: string }[]
  
  // Initialize data structures
  const hourCounts = new Map<number, number>()
  const dayCounts = new Map<string, number>()
  
  for (let i = 0; i < 24; i++) hourCounts.set(i, 0)
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  dayNames.forEach(day => dayCounts.set(day, 0))
  
  // Process logs
  logs.forEach(log => {
    const date = new Date(log.created_at)
    const hour = date.getUTCHours()
    const dayOfWeek = dayNames[date.getUTCDay()]
    
    hourCounts.set(hour, (hourCounts.get(hour) || 0) + 1)
    dayCounts.set(dayOfWeek, (dayCounts.get(dayOfWeek) || 0) + 1)
  })
  
  // Convert to arrays
  const byHour = Array.from(hourCounts.entries()).map(([hour, completions]) => ({ hour, completions }))
  const byDayOfWeek = dayNames.map(day => ({ day, completions: dayCounts.get(day) || 0 }))
  
  return {
    byHour,
    byDayOfWeek,
  }
}

// ============================================
// SHOP PURCHASE TRENDS
// ============================================

export async function getShopPurchaseTrends(days: number = 30): Promise<PurchaseTrend[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const now = new Date()
  const startDate = new Date(now)
  startDate.setDate(startDate.getDate() - days)
  
  const { data: purchasesData } = await supabase
    .from('shop_transactions')
    .select('item_type, shards_spent, created_at')
    .gte('created_at', startDate.toISOString())
  
  const purchases = (purchasesData || []) as { item_type: string; shards_spent: number; created_at: string }[]
  
  // Generate date labels
  const dateLabels: string[] = []
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now)
    d.setDate(d.getDate() - i)
    dateLabels.push(d.toISOString().split('T')[0])
  }
  
  // Aggregate by date
  const byDate = new Map<string, { shields: number; restTickets: number; totalShards: number }>()
  dateLabels.forEach(date => byDate.set(date, { shields: 0, restTickets: 0, totalShards: 0 }))
  
  purchases.forEach(p => {
    const date = new Date(p.created_at).toISOString().split('T')[0]
    const entry = byDate.get(date)
    if (entry) {
      if (p.item_type === 'streak_shield') entry.shields++
      else if (p.item_type === 'rest_ticket') entry.restTickets++
      entry.totalShards += p.shards_spent
    }
  })
  
  return dateLabels.map(date => {
    const entry = byDate.get(date) || { shields: 0, restTickets: 0, totalShards: 0 }
    return {
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      ...entry,
    }
  })
}

// ============================================
// TOP SPENDERS
// ============================================

export async function getTopSpenders(): Promise<TopSpender[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const { data: purchasesData } = await supabase
    .from('shop_transactions')
    .select('user_id, item_type, shards_spent')
  
  const purchases = (purchasesData || []) as { user_id: string; item_type: string; shards_spent: number }[]
  
  // Aggregate by user
  const userSpending = new Map<string, { totalSpent: number; shields: number; restTickets: number }>()
  purchases.forEach(p => {
    if (!userSpending.has(p.user_id)) {
      userSpending.set(p.user_id, { totalSpent: 0, shields: 0, restTickets: 0 })
    }
    const entry = userSpending.get(p.user_id)!
    entry.totalSpent += p.shards_spent
    if (p.item_type === 'streak_shield') entry.shields++
    else if (p.item_type === 'rest_ticket') entry.restTickets++
  })
  
  // Get user info
  const { data: profilesData } = await supabase
    .from('user_profiles')
    .select('id, display_name, email')
  
  const profiles = (profilesData || []) as { id: string; display_name: string | null; email: string | null }[]
  const profileMap = new Map(profiles.map(p => [p.id, p]))
  
  return Array.from(userSpending.entries())
    .map(([userId, data]) => {
      const profile = profileMap.get(userId)
      return {
        userId,
        displayName: profile?.display_name || 'Unknown',
        email: profile?.email || '',
        totalSpent: data.totalSpent,
        shieldsPurchased: data.shields,
        restTicketsPurchased: data.restTickets,
      }
    })
    .sort((a, b) => b.totalSpent - a.totalSpent)
    .slice(0, 20)
}

// ============================================
// TOP HABITS BY COMPLETIONS
// ============================================

export async function getTopHabitsByCompletions(): Promise<TopHabit[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const [habitsResult, logsResult, profilesResult] = await Promise.all([
    supabase.from('habits').select('id, title, emoji, user_id, streak_current'),
    supabase.from('evidence_logs').select('habit_id').in('ai_verdict', ['approved_gold', 'approved_silver', 'quick_complete']),
    supabase.from('user_profiles').select('id, display_name'),
  ])
  
  const habits = (habitsResult.data || []) as { id: string; title: string; emoji: string | null; user_id: string; streak_current: number }[]
  const logs = (logsResult.data || []) as { habit_id: string }[]
  const profiles = (profilesResult.data || []) as { id: string; display_name: string | null }[]
  
  const profileMap = new Map(profiles.map(p => [p.id, p.display_name || 'Unknown']))
  
  // Count completions per habit
  const completionCounts = new Map<string, number>()
  logs.forEach(l => {
    completionCounts.set(l.habit_id, (completionCounts.get(l.habit_id) || 0) + 1)
  })
  
  return habits
    .map(h => ({
      id: h.id,
      title: h.title,
      emoji: h.emoji,
      ownerName: profileMap.get(h.user_id) || 'Unknown',
      completions: completionCounts.get(h.id) || 0,
      currentStreak: h.streak_current || 0,
    }))
    .filter(h => h.completions > 0)
    .sort((a, b) => b.completions - a.completions)
    .slice(0, 20)
}

// ============================================
// BUG REPORTS
// ============================================

export async function getBugReports(filters?: {
  status?: string
  severity?: string
  category?: string
}): Promise<{ success: boolean; data?: BugReport[]; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  let query = supabase.from('bug_reports').select('*').order('created_at', { ascending: false })
  
  if (filters?.status && filters.status !== 'all') {
    query = query.eq('status', filters.status)
  }
  if (filters?.severity && filters.severity !== 'all') {
    query = query.eq('severity', filters.severity)
  }
  if (filters?.category && filters.category !== 'all') {
    query = query.eq('category', filters.category)
  }
  
  const { data, error } = await query
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true, data: data as BugReport[] }
}

export async function getBugReportStats(): Promise<{ success: boolean; stats?: BugStats; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const { data, error } = await supabase.from('bug_reports').select('status, severity, category')
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  const bugs = data || []
  
  const byStatus: Record<string, number> = {}
  const bySeverity: Record<string, number> = {}
  const byCategory: Record<string, number> = {}
  
  bugs.forEach((bug: any) => {
    byStatus[bug.status] = (byStatus[bug.status] || 0) + 1
    bySeverity[bug.severity] = (bySeverity[bug.severity] || 0) + 1
    byCategory[bug.category] = (byCategory[bug.category] || 0) + 1
  })
  
  return {
    success: true,
    stats: {
      total: bugs.length,
      byStatus,
      bySeverity,
      byCategory,
    }
  }
}

export async function updateBugReportStatus(
  bugId: string,
  status: 'open' | 'in_progress' | 'resolved' | 'closed' | 'duplicate' | 'wont_fix',
  adminNotes?: string
): Promise<{ success: boolean; error?: string }> {
  const { isAdmin: adminCheck, userId } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const updateData: any = {
    status,
    updated_at: new Date().toISOString(),
  }
  
  if (adminNotes !== undefined) {
    updateData.admin_notes = adminNotes
  }
  
  if (status === 'resolved' || status === 'closed') {
    updateData.resolved_at = new Date().toISOString()
    updateData.resolved_by = userId
  }
  
  const { error } = await supabase
    .from('bug_reports')
    .update(updateData)
    .eq('id', bugId)
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true }
}

// ============================================
// NOTIFICATIONS
// ============================================

export interface NotificationStats {
  totalSent: number
  sentToday: number
  sentThisWeek: number
  readRate: number
  byType: { type: string; count: number }[]
}

export interface NotificationConfig {
  id: string
  notification_type: string
  enabled: boolean
  description?: string
}

export async function getNotificationStats(): Promise<NotificationStats | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekAgo = new Date(todayStart)
  weekAgo.setDate(weekAgo.getDate() - 7)
  
  const { data: notificationsData } = await supabase.from('notifications').select('id, type, is_read, created_at')
  const notifications = (notificationsData || []) as { id: string; type: string; is_read: boolean; created_at: string }[]
  
  const sentToday = notifications.filter(n => new Date(n.created_at) >= todayStart).length
  const sentThisWeek = notifications.filter(n => new Date(n.created_at) >= weekAgo).length
  const readCount = notifications.filter(n => n.is_read).length
  const readRate = notifications.length > 0 ? Math.round((readCount / notifications.length) * 1000) / 10 : 0
  
  // Group by type
  const typeCounts = new Map<string, number>()
  notifications.forEach(n => {
    typeCounts.set(n.type, (typeCounts.get(n.type) || 0) + 1)
  })
  
  const byType = Array.from(typeCounts.entries()).map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count)
  
  return {
    totalSent: notifications.length,
    sentToday,
    sentThisWeek,
    readRate,
    byType,
  }
}

export async function getNotificationConfig(): Promise<NotificationConfig[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const { data } = await supabase.from('notification_settings').select('*')
  
  // If no settings exist, return default config
  if (!data || data.length === 0) {
    return [
      { id: '1', notification_type: 'reaction', enabled: true, description: 'When someone reacts to your milestone' },
      { id: '2', notification_type: 'comment', enabled: true, description: 'When someone comments on your milestone' },
      { id: '3', notification_type: 'milestone', enabled: true, description: 'Streak milestone notifications' },
      { id: '4', notification_type: 'conquest', enabled: true, description: 'Habit conquest notifications' },
      { id: '5', notification_type: 'new_follower', enabled: true, description: 'When someone follows you' },
      { id: '6', notification_type: 'challenge_completed', enabled: true, description: 'Challenge completion notifications' },
    ]
  }
  
  return data as NotificationConfig[]
}

export async function updateNotificationConfig(
  notificationType: string,
  enabled: boolean
): Promise<{ success: boolean; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const { error } = await supabase
    .from('notification_settings')
    .upsert({ 
      notification_type: notificationType, 
      enabled,
      updated_at: new Date().toISOString()
    }, { onConflict: 'notification_type' })
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true }
}

// ============================================
// PUSH NOTIFICATIONS
// ============================================

export interface PushStats {
  subscribedUsers: number
  totalSent: number
  sentToday: number
  totalBroadcasts: number
  activeQuotes: number
  totalQuotesSent: number
}

export interface PushBroadcast {
  id: string
  sender_id: string
  recipient_type: 'all' | 'specific'
  recipient_id: string | null
  recipient_username?: string
  title: string
  body: string | null
  icon: string | null
  total_recipients: number
  successful_sends: number
  failed_sends: number
  sent_at: string
}

export interface PushUser {
  id: string
  username: string | null
  display_name: string | null
}

export type QuoteCategory = 'motivational' | 'funny' | 'encouraging' | 'urgent' | 'playful'

export interface NotificationQuote {
  id: string
  text: string
  category: QuoteCategory
  emoji: string
  is_active: boolean
  usage_count: number
  created_at: string
  updated_at: string
}

export interface QuoteStats {
  total: number
  active: number
  byCategory: Record<QuoteCategory, number>
  totalUsage: number
}

export async function getPushStats(): Promise<PushStats | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  
  const [usersResult, broadcastsResult, quotesResult] = await Promise.all([
    supabase.from('user_profiles').select('id').eq('push_enabled', true),
    supabase.from('push_broadcasts').select('id, successful_sends, sent_at'),
    supabase.from('notification_quotes').select('id, is_active, usage_count'),
  ])
  
  const users = (usersResult.data || []) as { id: string }[]
  const broadcasts = (broadcastsResult.data || []) as { id: string; successful_sends: number; sent_at: string }[]
  const quotes = (quotesResult.data || []) as { id: string; is_active: boolean; usage_count: number }[]
  
  const totalSent = broadcasts.reduce((sum, b) => sum + (b.successful_sends || 0), 0)
  const sentToday = broadcasts
    .filter(b => new Date(b.sent_at) >= todayStart)
    .reduce((sum, b) => sum + (b.successful_sends || 0), 0)
  
  return {
    subscribedUsers: users.length,
    totalSent,
    sentToday,
    totalBroadcasts: broadcasts.length,
    activeQuotes: quotes.filter(q => q.is_active).length,
    totalQuotesSent: quotes.reduce((sum, q) => sum + (q.usage_count || 0), 0),
  }
}

export async function getUsersWithPush(): Promise<PushUser[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const { data } = await supabase
    .from('user_profiles')
    .select('id, username, display_name')
    .eq('push_enabled', true)
    .order('username')
  
  return (data || []) as PushUser[]
}

export async function getPushBroadcastHistory(): Promise<PushBroadcast[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const { data: broadcasts } = await supabase
    .from('push_broadcasts')
    .select('*')
    .order('sent_at', { ascending: false })
    .limit(50)
  
  if (!broadcasts || broadcasts.length === 0) return []
  
  // Get recipient usernames for specific sends
  const recipientIds = broadcasts
    .filter((b: any) => b.recipient_id)
    .map((b: any) => b.recipient_id)
  
  let recipientMap: Record<string, string> = {}
  if (recipientIds.length > 0) {
    const { data: recipients } = await supabase
      .from('user_profiles')
      .select('id, username')
      .in('id', recipientIds)
    
    if (recipients) {
      recipientMap = Object.fromEntries(
        recipients.map((r: any) => [r.id, r.username])
      )
    }
  }
  
  return broadcasts.map((b: any) => ({
    ...b,
    recipient_username: b.recipient_id ? recipientMap[b.recipient_id] : undefined,
  })) as PushBroadcast[]
}

// API Base URL for server-side operations
const PUSH_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.miac.app'

async function callPushAPI(body: object): Promise<{ success: boolean; totalSent?: number; totalFailed?: number; totalUsers?: number; sentCount?: number; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }

  try {
    const { data: { session } } = await supabase.auth.getSession()
    
    const response = await fetch(`${PUSH_API_BASE_URL}/api/push/broadcast`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify(body),
    })
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Push API Error:', error)
    return { success: false, error: 'Network error. Please check your connection.' }
  }
}

export async function sendPushBroadcast(
  title: string,
  body: string,
  icon?: string
): Promise<{ success: boolean; totalSent?: number; totalFailed?: number; totalUsers?: number; error?: string }> {
  return callPushAPI({
    type: 'all',
    title,
    body,
    icon: icon || '🔔',
  })
}

export async function sendPushToUser(
  targetUserId: string,
  title: string,
  body: string,
  icon?: string
): Promise<{ success: boolean; sentCount?: number; error?: string }> {
  return callPushAPI({
    type: 'specific',
    targetUserId,
    title,
    body,
    icon: icon || '🔔',
  })
}

// ============================================
// NOTIFICATION QUOTES
// ============================================

export async function getNotificationQuotes(): Promise<NotificationQuote[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const { data } = await supabase
    .from('notification_quotes')
    .select('*')
    .order('category')
    .order('created_at', { ascending: false })
  
  return (data || []) as NotificationQuote[]
}

export async function getQuoteStats(): Promise<QuoteStats | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const { data: quotes } = await supabase
    .from('notification_quotes')
    .select('*')
  
  if (!quotes) return null
  
  const typedQuotes = quotes as NotificationQuote[]
  
  return {
    total: typedQuotes.length,
    active: typedQuotes.filter(q => q.is_active).length,
    byCategory: {
      motivational: typedQuotes.filter(q => q.category === 'motivational').length,
      funny: typedQuotes.filter(q => q.category === 'funny').length,
      encouraging: typedQuotes.filter(q => q.category === 'encouraging').length,
      urgent: typedQuotes.filter(q => q.category === 'urgent').length,
      playful: typedQuotes.filter(q => q.category === 'playful').length,
    },
    totalUsage: typedQuotes.reduce((sum, q) => sum + (q.usage_count || 0), 0),
  }
}

export async function createNotificationQuote(
  text: string,
  category: QuoteCategory,
  emoji: string
): Promise<{ success: boolean; quote?: NotificationQuote; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const { data, error } = await supabase
    .from('notification_quotes')
    .insert({
      text: text.trim(),
      category,
      emoji: emoji || '💪',
      is_active: true,
    })
    .select()
    .single()
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true, quote: data as NotificationQuote }
}

export async function updateNotificationQuote(
  id: string,
  updates: { text?: string; category?: QuoteCategory; emoji?: string; is_active?: boolean }
): Promise<{ success: boolean; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const updateData: any = { updated_at: new Date().toISOString() }
  if (updates.text !== undefined) updateData.text = updates.text.trim()
  if (updates.category !== undefined) updateData.category = updates.category
  if (updates.emoji !== undefined) updateData.emoji = updates.emoji
  if (updates.is_active !== undefined) updateData.is_active = updates.is_active
  
  const { error } = await supabase
    .from('notification_quotes')
    .update(updateData)
    .eq('id', id)
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true }
}

export async function deleteNotificationQuote(id: string): Promise<{ success: boolean; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const { error } = await supabase
    .from('notification_quotes')
    .delete()
    .eq('id', id)
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true }
}

// ============================================
// CHALLENGES ADMIN
// ============================================

export interface AdminChallenge {
  id: string
  title: string
  description: string
  category: string
  duration_days: number
  badge_emoji: string | null
  is_active: boolean
  is_strict: boolean
  habitsCount: number
  created_at: string
}

export interface ChallengeAnalytics {
  totalParticipants: number
  completions: number
  completionRate: number
}

export type ChallengeAnalyticsMap = Record<string, ChallengeAnalytics>

export async function getAllChallengesAdmin(): Promise<AdminChallenge[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const [challengesResult, habitsResult] = await Promise.all([
    supabase.from('challenges').select('*').order('created_at', { ascending: false }),
    supabase.from('challenge_habits').select('challenge_id'),
  ])
  
  const challenges = (challengesResult.data || []) as any[]
  const challengeHabits = (habitsResult.data || []) as { challenge_id: string }[]
  
  // Count habits per challenge
  const habitCounts = new Map<string, number>()
  challengeHabits.forEach(ch => {
    habitCounts.set(ch.challenge_id, (habitCounts.get(ch.challenge_id) || 0) + 1)
  })
  
  return challenges.map(c => ({
    id: c.id,
    title: c.title,
    description: c.description || '',
    category: c.category || 'general',
    duration_days: c.duration_days || 30,
    badge_emoji: c.badge_emoji,
    is_active: c.is_active ?? true,
    is_strict: c.is_strict ?? false,
    habitsCount: habitCounts.get(c.id) || 0,
    created_at: c.created_at,
  }))
}

export async function getChallengeAnalyticsAll(): Promise<ChallengeAnalyticsMap> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return {}
  
  const [challengesResult, userChallengesResult] = await Promise.all([
    supabase.from('challenges').select('id'),
    supabase.from('user_challenges').select('challenge_id, status'),
  ])
  
  const challenges = (challengesResult.data || []) as { id: string }[]
  const participations = (userChallengesResult.data || []) as { challenge_id: string; status: string }[]
  
  const analyticsMap: ChallengeAnalyticsMap = {}
  
  challenges.forEach(c => {
    const cParticipations = participations.filter(p => p.challenge_id === c.id)
    const completed = cParticipations.filter(p => p.status === 'completed').length
    
    analyticsMap[c.id] = {
      totalParticipants: cParticipations.length,
      completions: completed,
      completionRate: cParticipations.length > 0 ? Math.round((completed / cParticipations.length) * 1000) / 10 : 0,
    }
  })
  
  return analyticsMap
}

export async function toggleChallengeActive(
  challengeId: string,
  isActive: boolean
): Promise<{ success: boolean; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const { error } = await supabase
    .from('challenges')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', challengeId)
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true }
}

// ============================================
// BADGES ADMIN
// ============================================

export type BadgeCategory = 'behavior' | 'milestone' | 'social'
export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary'

export interface AdminBadge {
  id: string
  code: string
  name: string
  description: string
  emoji: string
  category: BadgeCategory
  rarity: BadgeRarity
  xp_reward: number
  is_active: boolean
  earnedCount: number
  created_at: string
}

export interface BadgeStatistics {
  totalBadges: number
  activeBadges: number
  totalAwarded: number
  categoryCounts: Record<string, number>
  rarityCounts: Record<string, number>
  mostEarned: { name: string; emoji: string; count: number } | null
  leastEarned: { name: string; emoji: string; count: number } | null
}

export async function getAllBadgesAdmin(): Promise<AdminBadge[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const [badgesResult, earnedResult] = await Promise.all([
    supabase.from('badge_definitions').select('*').order('created_at', { ascending: false }),
    supabase.from('user_badges').select('badge_id'),
  ])
  
  const badges = (badgesResult.data || []) as any[]
  const earned = (earnedResult.data || []) as { badge_id: string }[]
  
  // Count earned per badge
  const earnedCounts = new Map<string, number>()
  earned.forEach(e => {
    earnedCounts.set(e.badge_id, (earnedCounts.get(e.badge_id) || 0) + 1)
  })
  
  return badges.map(b => ({
    id: b.id,
    code: b.code,
    name: b.name,
    description: b.description || '',
    emoji: b.emoji || '🏆',
    category: b.category || 'behavior',
    rarity: b.rarity || 'common',
    xp_reward: b.xp_reward || 0,
    is_active: b.is_active ?? true,
    earnedCount: earnedCounts.get(b.id) || 0,
    created_at: b.created_at,
  }))
}

export async function getBadgeStatistics(): Promise<BadgeStatistics | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const [badgesResult, earnedResult] = await Promise.all([
    supabase.from('badge_definitions').select('*'),
    supabase.from('user_badges').select('badge_id'),
  ])
  
  const badges = (badgesResult.data || []) as any[]
  const earned = (earnedResult.data || []) as { badge_id: string }[]
  
  const activeBadges = badges.filter(b => b.is_active !== false).length
  
  const categoryCounts: Record<string, number> = {}
  const rarityCounts: Record<string, number> = {}
  
  badges.forEach(b => {
    const cat = b.category || 'behavior'
    const rarity = b.rarity || 'common'
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1
    rarityCounts[rarity] = (rarityCounts[rarity] || 0) + 1
  })
  
  // Count earned per badge
  const earnedCounts = new Map<string, number>()
  earned.forEach(e => {
    earnedCounts.set(e.badge_id, (earnedCounts.get(e.badge_id) || 0) + 1)
  })
  
  // Find most and least earned
  let mostEarned: { name: string; emoji: string; count: number } | null = null
  let leastEarned: { name: string; emoji: string; count: number } | null = null
  let maxCount = 0
  let minCount = Infinity
  
  badges.forEach(b => {
    const count = earnedCounts.get(b.id) || 0
    if (count > maxCount) {
      maxCount = count
      mostEarned = { name: b.name, emoji: b.emoji || '🏆', count }
    }
    if (count < minCount && count > 0) {
      minCount = count
      leastEarned = { name: b.name, emoji: b.emoji || '🏆', count }
    }
  })
  
  return {
    totalBadges: badges.length,
    activeBadges,
    totalAwarded: earned.length,
    categoryCounts,
    rarityCounts,
    mostEarned,
    leastEarned,
  }
}

export async function toggleBadgeActive(
  badgeId: string,
  isActive: boolean
): Promise<{ success: boolean; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const { error } = await supabase
    .from('badge_definitions')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', badgeId)
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true }
}

// ============================================
// BI / FEATURE ADOPTION METRICS
// ============================================

export interface BIFeatureAdoption {
  totalUsers: number
  // Notifications
  pushEnabled: { count: number; percentage: number }
  emailNotificationsEnabled: { count: number; percentage: number }
  // Widgets & Lock Screen
  widgetEnabled: { count: number; percentage: number }
  lockScreenEnabled: { count: number; percentage: number }
  // Dashboard View Mode
  dashboardViewMode: {
    compact: { count: number; percentage: number }
    monthly: { count: number; percentage: number }
    expanded: { count: number; percentage: number }
    notSet: { count: number; percentage: number }
  }
  // Themes
  themePreset: {
    terminal: { count: number; percentage: number }
    dawn: { count: number; percentage: number }
    ocean: { count: number; percentage: number }
    custom: { count: number; percentage: number }
    notSet: { count: number; percentage: number }
  }
  // Fonts
  fontChoice: {
    inter: { count: number; percentage: number }
    mono: { count: number; percentage: number }
    system: { count: number; percentage: number }
    notSet: { count: number; percentage: number }
  }
  // Profile Customization
  hasProfileEmoji: { count: number; percentage: number }
  hasCustomAccentColor: { count: number; percentage: number }
  hasBio: { count: number; percentage: number }
  isPublicProfile: { count: number; percentage: number }
  // Habit Features
  hasCardColors: { count: number; percentage: number }
  habitCountDistribution: { range: string; count: number }[]
  // Time-based
  notificationTimeDistribution: { hour: string; count: number }[]
}

export async function getUserSettingsBI(): Promise<BIFeatureAdoption | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null

  const [profilesResult, habitsResult] = await Promise.all([
    supabase.from('user_profiles').select(
      'id, push_enabled, notification_email_enabled, has_widget_enabled, has_lock_screen_enabled, dashboard_view_mode, theme_preset, accent_color, font_choice, profile_emoji, bio, is_public, notification_time'
    ),
    supabase.from('habits').select('user_id, card_color'),
  ])

  const profiles = (profilesResult.data || []) as {
    id: string
    push_enabled: boolean
    notification_email_enabled: boolean
    has_widget_enabled: boolean
    has_lock_screen_enabled: boolean
    dashboard_view_mode: string | null
    theme_preset: string | null
    accent_color: string | null
    font_choice: string | null
    profile_emoji: string | null
    bio: string | null
    is_public: boolean
    notification_time: string | null
  }[]

  const habits = (habitsResult.data || []) as { user_id: string; card_color: string | null }[]

  const total = profiles.length
  const pct = (count: number) => total > 0 ? Math.round((count / total) * 1000) / 10 : 0

  // Notifications
  const pushCount = profiles.filter(p => p.push_enabled).length
  const emailCount = profiles.filter(p => p.notification_email_enabled).length

  // Widgets & Lock Screen
  const widgetCount = profiles.filter(p => p.has_widget_enabled).length
  const lockScreenCount = profiles.filter(p => p.has_lock_screen_enabled).length

  // Dashboard View Mode
  const compact = profiles.filter(p => p.dashboard_view_mode === 'compact').length
  const monthly = profiles.filter(p => p.dashboard_view_mode === 'monthly').length
  const expanded = profiles.filter(p => p.dashboard_view_mode === 'expanded').length
  const viewNotSet = profiles.filter(p => !p.dashboard_view_mode).length

  // Theme
  const terminal = profiles.filter(p => p.theme_preset === 'terminal').length
  const dawn = profiles.filter(p => p.theme_preset === 'dawn').length
  const ocean = profiles.filter(p => p.theme_preset === 'ocean').length
  const customTheme = profiles.filter(p => p.theme_preset === 'custom').length
  const themeNotSet = profiles.filter(p => !p.theme_preset).length

  // Fonts
  const inter = profiles.filter(p => p.font_choice === 'inter').length
  const mono = profiles.filter(p => p.font_choice === 'mono').length
  const system = profiles.filter(p => p.font_choice === 'system').length
  const fontNotSet = profiles.filter(p => !p.font_choice).length

  // Profile customization
  const hasEmoji = profiles.filter(p => p.profile_emoji).length
  const hasAccent = profiles.filter(p => p.accent_color && p.accent_color !== '#22c55e').length
  const hasBio = profiles.filter(p => p.bio && p.bio.trim().length > 0).length
  const isPublic = profiles.filter(p => p.is_public).length

  // Habit features
  const usersWithCardColors = new Set(habits.filter(h => h.card_color).map(h => h.user_id)).size

  // Habit count distribution
  const habitCounts = new Map<string, number>()
  habits.forEach(h => {
    habitCounts.set(h.user_id, (habitCounts.get(h.user_id) || 0) + 1)
  })

  const habitRanges = [
    { min: 0, max: 0, label: '0 habits' },
    { min: 1, max: 1, label: '1 habit' },
    { min: 2, max: 3, label: '2-3 habits' },
    { min: 4, max: 6, label: '4-6 habits' },
    { min: 7, max: 10, label: '7-10 habits' },
    { min: 11, max: Infinity, label: '11+ habits' },
  ]

  const habitCountDistribution = habitRanges.map(range => {
    const count = profiles.filter(p => {
      const hCount = habitCounts.get(p.id) || 0
      return hCount >= range.min && hCount <= range.max
    }).length
    return { range: range.label, count }
  })

  // Notification time distribution
  const timeCounts = new Map<string, number>()
  profiles.forEach(p => {
    if (p.notification_time) {
      const hour = p.notification_time.split(':')[0] + ':00'
      timeCounts.set(hour, (timeCounts.get(hour) || 0) + 1)
    }
  })

  const notificationTimeDistribution = Array.from(timeCounts.entries())
    .map(([hour, count]) => ({ hour, count }))
    .sort((a, b) => a.hour.localeCompare(b.hour))

  return {
    totalUsers: total,
    pushEnabled: { count: pushCount, percentage: pct(pushCount) },
    emailNotificationsEnabled: { count: emailCount, percentage: pct(emailCount) },
    widgetEnabled: { count: widgetCount, percentage: pct(widgetCount) },
    lockScreenEnabled: { count: lockScreenCount, percentage: pct(lockScreenCount) },
    dashboardViewMode: {
      compact: { count: compact, percentage: pct(compact) },
      monthly: { count: monthly, percentage: pct(monthly) },
      expanded: { count: expanded, percentage: pct(expanded) },
      notSet: { count: viewNotSet, percentage: pct(viewNotSet) },
    },
    themePreset: {
      terminal: { count: terminal, percentage: pct(terminal) },
      dawn: { count: dawn, percentage: pct(dawn) },
      ocean: { count: ocean, percentage: pct(ocean) },
      custom: { count: customTheme, percentage: pct(customTheme) },
      notSet: { count: themeNotSet, percentage: pct(themeNotSet) },
    },
    fontChoice: {
      inter: { count: inter, percentage: pct(inter) },
      mono: { count: mono, percentage: pct(mono) },
      system: { count: system, percentage: pct(system) },
      notSet: { count: fontNotSet, percentage: pct(fontNotSet) },
    },
    hasProfileEmoji: { count: hasEmoji, percentage: pct(hasEmoji) },
    hasCustomAccentColor: { count: hasAccent, percentage: pct(hasAccent) },
    hasBio: { count: hasBio, percentage: pct(hasBio) },
    isPublicProfile: { count: isPublic, percentage: pct(isPublic) },
    hasCardColors: { count: usersWithCardColors, percentage: pct(usersWithCardColors) },
    habitCountDistribution,
    notificationTimeDistribution,
  }
}

// ============================================
// GOVEE LIGHTS
// ============================================

// API Base URL for Govee calls (goes through Next.js server)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.miac.app'

export interface GoveeEventConfig {
  event_type: string
  enabled: boolean
  color: string
  duration_seconds: number
  priority: number
  description: string
}

export interface GoveeSettings {
  master_enabled: boolean
  queue_processing_enabled: boolean
  default_color: string
  default_brightness: number
}

export interface GoveeEventStats {
  todayCount: number
  weekCount: number
  pendingCount: number
  eventTypeCounts: Record<string, { total: number; completed: number; failed: number }>
  recentEvents: any[]
}

// Helper function to call Govee API through Next.js server
async function callGoveeControlAPI(body: object): Promise<{ success: boolean; message: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, message: 'Unauthorized' }

  try {
    // Get current session for auth
    const { data: { session } } = await supabase.auth.getSession()
    
    const response = await fetch(`${API_BASE_URL}/api/govee/control`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Pass auth cookie/token if available
        ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(body),
    })

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Govee API Error:', error)
    return { success: false, message: 'Network error. Please check your connection.' }
  }
}

export async function turnLightOn(): Promise<{ success: boolean; message: string }> {
  return callGoveeControlAPI({ action: 'on' })
}

export async function turnLightOff(): Promise<{ success: boolean; message: string }> {
  return callGoveeControlAPI({ action: 'off' })
}

export async function setLightColor(r: number, g: number, b: number): Promise<{ success: boolean; message: string }> {
  return callGoveeControlAPI({ action: 'color', r, g, b })
}

export async function setLightBrightness(brightness: number): Promise<{ success: boolean; message: string }> {
  const clampedBrightness = Math.max(0, Math.min(100, brightness))
  return callGoveeControlAPI({ action: 'brightness', brightness: clampedBrightness })
}

export async function flashLight(times: number): Promise<{ success: boolean; message: string }> {
  return callGoveeControlAPI({ action: 'flash', times })
}

export async function celebrationMode(): Promise<{ success: boolean; message: string }> {
  return callGoveeControlAPI({ action: 'celebration' })
}

export async function getDeviceState(): Promise<{ success: boolean; message: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, message: 'Unauthorized' }

  try {
    const { data: { session } } = await supabase.auth.getSession()
    
    const response = await fetch(`${API_BASE_URL}/api/govee/status`, {
      method: 'GET',
      headers: {
        ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
      },
      credentials: 'include',
    })

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Govee Status Error:', error)
    return { success: false, message: 'Network error' }
  }
}

export async function getEventConfigs(): Promise<GoveeEventConfig[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []
  
  const { data } = await supabase.from('govee_event_config').select('*')
  
  if (!data || data.length === 0) {
    // Return default configs
    return [
      { event_type: 'new_signup', enabled: true, color: '#00FF00', duration_seconds: 5, priority: 1, description: 'New user signup' },
      { event_type: 'habit_completion', enabled: true, color: '#0000FF', duration_seconds: 3, priority: 2, description: 'Habit completed' },
      { event_type: 'gold_completion', enabled: true, color: '#FFD700', duration_seconds: 5, priority: 3, description: 'Gold verification' },
      { event_type: 'conquest', enabled: true, color: '#FF00FF', duration_seconds: 8, priority: 5, description: 'Habit conquered' },
      { event_type: 'streak_milestone', enabled: true, color: '#FF6600', duration_seconds: 6, priority: 4, description: 'Streak milestone reached' },
      { event_type: 'first_habit', enabled: true, color: '#00FFFF', duration_seconds: 5, priority: 3, description: 'First habit created' },
      { event_type: 'challenge_join', enabled: true, color: '#9900FF', duration_seconds: 4, priority: 2, description: 'Challenge joined' },
      { event_type: 'challenge_complete', enabled: true, color: '#FF0000', duration_seconds: 7, priority: 5, description: 'Challenge completed' },
    ]
  }
  
  return data as GoveeEventConfig[]
}

export async function updateEventConfig(
  eventType: string,
  updates: Partial<GoveeEventConfig>
): Promise<{ success: boolean; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const { error } = await supabase
    .from('govee_event_config')
    .upsert({ 
      event_type: eventType,
      ...updates,
      updated_at: new Date().toISOString()
    }, { onConflict: 'event_type' })
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true }
}

export async function getGoveeSettings(): Promise<GoveeSettings | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const { data } = await supabase.from('govee_settings').select('*').single()
  
  if (!data) {
    // Return default settings
    return {
      master_enabled: true,
      queue_processing_enabled: true,
      default_color: '#FFFFFF',
      default_brightness: 100,
    }
  }
  
  return data as GoveeSettings
}

export async function updateGoveeSettings(
  updates: Partial<GoveeSettings>
): Promise<{ success: boolean; error?: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, error: 'Unauthorized' }
  
  const { error } = await supabase
    .from('govee_settings')
    .upsert({ 
      id: 1, // Single settings row
      ...updates,
      updated_at: new Date().toISOString()
    })
  
  if (error) {
    return { success: false, error: error.message }
  }
  
  return { success: true }
}

export async function getEventStats(): Promise<GoveeEventStats | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null
  
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekAgo = new Date(todayStart)
  weekAgo.setDate(weekAgo.getDate() - 7)
  
  const { data: eventsData } = await supabase
    .from('govee_event_log')
    .select('*')
    .order('triggered_at', { ascending: false })
  
  const events = (eventsData || []) as any[]
  
  const todayCount = events.filter(e => new Date(e.triggered_at) >= todayStart).length
  const weekCount = events.filter(e => new Date(e.triggered_at) >= weekAgo).length
  const pendingCount = events.filter(e => e.status === 'pending').length
  
  // Count by event type
  const eventTypeCounts: Record<string, { total: number; completed: number; failed: number }> = {}
  events.forEach(e => {
    if (!eventTypeCounts[e.event_type]) {
      eventTypeCounts[e.event_type] = { total: 0, completed: 0, failed: 0 }
    }
    eventTypeCounts[e.event_type].total++
    if (e.status === 'completed') eventTypeCounts[e.event_type].completed++
    if (e.status === 'failed') eventTypeCounts[e.event_type].failed++
  })
  
  return {
    todayCount,
    weekCount,
    pendingCount,
    eventTypeCounts,
    recentEvents: events.slice(0, 20),
  }
}

export async function queueLightEvent(eventType: string): Promise<{ success: boolean; message: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, message: 'Unauthorized' }
  
  const { error } = await supabase.from('govee_event_log').insert({
    event_type: eventType,
    status: 'pending',
    triggered_at: new Date().toISOString(),
  })
  
  if (error) {
    return { success: false, message: error.message }
  }
  
  return { success: true, message: 'Event queued' }
}

export async function processNextEvent(): Promise<{ success: boolean; message: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, message: 'Unauthorized' }
  
  // Get next pending event
  const { data, error } = await supabase
    .from('govee_event_log')
    .select('*')
    .eq('status', 'pending')
    .order('triggered_at', { ascending: true })
    .limit(1)
    .single()
  
  if (error || !data) {
    return { success: false, message: 'No pending events' }
  }
  
  // Mark as completed
  await supabase
    .from('govee_event_log')
    .update({ status: 'completed', processed_at: new Date().toISOString() })
    .eq('id', data.id)
  
  return { success: true, message: 'Event processed' }
}

export async function cleanupExpiredEvents(): Promise<{ success: boolean; message: string }> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return { success: false, message: 'Unauthorized' }
  
  // Mark old pending events as expired
  const fiveMinutesAgo = new Date()
  fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5)
  
  const { error } = await supabase
    .from('govee_event_log')
    .update({ status: 'expired' })
    .eq('status', 'pending')
    .lt('triggered_at', fiveMinutesAgo.toISOString())
  
  if (error) {
    return { success: false, message: error.message }
  }
  
  return { success: true, message: 'Expired events cleaned up' }
}

// ============================================
// SUBSCRIPTION ADMIN
// ============================================

export interface SubscriptionMetrics {
  totalPremium: number
  totalFree: number
  premiumByProvider: { apple: number; google: number; lemonsqueezy: number; grandfathered: number }
  mrr: number
  totalRevenue: number
  conversionRate: number
  avgVerificationsUsed: number
  usersHittingLimit: number
}

export interface SubscriptionEvent {
  id: string
  user_id: string
  event_type: string
  provider: string
  plan_id: string | null
  amount: number | null
  currency: string | null
  created_at: string
  user_email?: string
  user_name?: string
}

export interface SubscriberInfo {
  id: string
  display_name: string | null
  username: string | null
  subscription_tier: string
  subscription_provider: string | null
  subscription_status: string | null
  subscription_expires_at: string | null
  free_verifications_used: number
  purchased_verifications: number
  created_at: string
}

export async function getSubscriptionMetrics(): Promise<SubscriptionMetrics | null> {
  if (!(await isAdmin())) return null

  const { data: profiles, error } = await supabase
    .from('user_profiles')
    .select('id, subscription_tier, subscription_provider, subscription_status, subscription_expires_at, free_verifications_used')

  if (error || !profiles) return null

  const premium = profiles.filter(p => p.subscription_tier === 'premium')
  const free = profiles.filter(p => p.subscription_tier === 'free')

  // Count by provider
  const grandfathered = premium.filter(p => !p.subscription_provider).length
  const apple = premium.filter(p => p.subscription_provider === 'apple').length
  const google = premium.filter(p => p.subscription_provider === 'google').length
  const lemonsqueezy = premium.filter(p => p.subscription_provider === 'lemonsqueezy').length

  // Calculate real MRR from actual payment data in subscription_events
  const activeSubscribers = premium.filter(p =>
    p.subscription_provider && p.subscription_status === 'active'
  )

  let mrr = 0

  if (activeSubscribers.length > 0) {
    const activeUserIds = activeSubscribers.map(p => p.id)

    const paymentEventTypes = [
      'INITIAL_PURCHASE', 'RENEWAL',
      'subscription_created', 'subscription_payment_success',
    ]

    const { data: events } = await supabase
      .from('subscription_events')
      .select('user_id, amount, event_type, created_at')
      .in('user_id', activeUserIds)
      .in('event_type', paymentEventTypes)
      .not('amount', 'is', null)
      .order('created_at', { ascending: false })

    if (events && events.length > 0) {
      const latestPaymentByUser = new Map<string, number>()
      for (const event of events) {
        if (!latestPaymentByUser.has(event.user_id) && event.amount) {
          latestPaymentByUser.set(event.user_id, event.amount)
        }
      }

      for (const [, amount] of latestPaymentByUser) {
        if (amount > 15) {
          mrr += amount / 12
        } else {
          mrr += amount
        }
      }

      // Fallback for active subscribers with no payment events recorded
      const subscribersWithPayments = new Set(latestPaymentByUser.keys())
      const subscribersWithoutPayments = activeSubscribers.filter(p => !subscribersWithPayments.has(p.id))
      mrr += subscribersWithoutPayments.length * 5.99
    } else {
      mrr = activeSubscribers.length * 5.99
    }
  }

  // Total revenue: sum all actual payments ever received
  // Use subscription_payment_success for LemonSqueezy (not order_created to avoid double-counting)
  // Use INITIAL_PURCHASE/RENEWAL for RevenueCat
  const revenueEventTypes = [
    'INITIAL_PURCHASE', 'RENEWAL',
    'subscription_payment_success',
  ]
  const { data: allPayments } = await supabase
    .from('subscription_events')
    .select('amount, event_type')
    .in('event_type', revenueEventTypes)
    .not('amount', 'is', null)

  const totalRevenue = allPayments
    ? allPayments.reduce((sum, e) => sum + (parseFloat(String(e.amount)) || 0), 0)
    : 0

  // Conversion rate
  const conversionRate = profiles.length > 0
    ? (premium.length / profiles.length) * 100
    : 0

  // Verification usage (free users only)
  const freeWithVerifications = free.filter(p => p.free_verifications_used > 0)
  const avgVerificationsUsed = freeWithVerifications.length > 0
    ? freeWithVerifications.reduce((sum, p) => sum + p.free_verifications_used, 0) / freeWithVerifications.length
    : 0
  const usersHittingLimit = free.filter(p => p.free_verifications_used >= 14).length

  return {
    totalPremium: premium.length,
    totalFree: free.length,
    premiumByProvider: { apple, google, lemonsqueezy, grandfathered },
    mrr,
    totalRevenue,
    conversionRate,
    avgVerificationsUsed,
    usersHittingLimit,
  }
}

export async function getSubscriptionEvents(limit = 50): Promise<SubscriptionEvent[]> {
  if (!(await isAdmin())) return []

  const { data, error } = await supabase
    .from('subscription_events')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error || !data) return []

  // Fetch user info for events
  const userIds = [...new Set(data.map(e => e.user_id))]
  const { data: users } = await supabase
    .from('user_profiles')
    .select('id, display_name, username')
    .in('id', userIds)

  const userMap = new Map(users?.map(u => [u.id, u]) || [])

  return data.map(event => ({
    ...event,
    user_name: userMap.get(event.user_id)?.display_name || userMap.get(event.user_id)?.username || 'Unknown',
  }))
}

export async function getSubscriberList(search?: string): Promise<SubscriberInfo[]> {
  if (!(await isAdmin())) return []

  let query = supabase
    .from('user_profiles')
    .select('id, display_name, username, subscription_tier, subscription_provider, subscription_status, subscription_expires_at, free_verifications_used, purchased_verifications, created_at')
    .order('subscription_tier', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(200)

  if (search) {
    query = query.or(`display_name.ilike.%${search}%,username.ilike.%${search}%`)
  }

  const { data, error } = await query
  if (error || !data) return []
  return data
}

export async function grantPremium(userId: string, days: number): Promise<boolean> {
  if (!(await isAdmin())) return false

  const { error } = await supabase.rpc('admin_grant_premium', {
    target_user_id: userId,
    days,
  })

  if (error) {
    console.error('[Admin] grantPremium failed:', error.message)
    return false
  }

  return true
}

export async function grantVerifications(userId: string, count: number): Promise<boolean> {
  if (!(await isAdmin())) return false

  const { data, error } = await supabase.rpc('grant_verifications', {
    target_user_id: userId,
    verification_count: count,
  })

  // Fallback if RPC doesn't exist - use direct update
  if (error) {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('purchased_verifications')
      .eq('id', userId)
      .single()

    if (!profile) return false

    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({ purchased_verifications: (profile.purchased_verifications ?? 0) + count })
      .eq('id', userId)

    return !updateError
  }

  return true
}

// ============================================
// ANALYTICS: BI EVENTS & FUNNELS
// ============================================

export interface EventCount {
  event_name: string
  event_category: string
  count: number
  unique_users: number
}

export interface EventTrendPoint {
  date: string
  count: number
}

export interface FunnelStep {
  step: string
  step_index: number
  count: number
  unique_users: number
}

export type DateRange = 'today' | '7d' | '30d' | '90d'

function getDateRangeStart(range: DateRange): Date {
  const now = new Date()
  switch (range) {
    case 'today': {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      return d
    }
    case '7d': {
      const d = new Date(now)
      d.setDate(d.getDate() - 7)
      return d
    }
    case '30d': {
      const d = new Date(now)
      d.setDate(d.getDate() - 30)
      return d
    }
    case '90d': {
      const d = new Date(now)
      d.setDate(d.getDate() - 90)
      return d
    }
  }
}

export async function getEventCounts(
  dateRange: DateRange,
  category?: string
): Promise<EventCount[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []

  const start = getDateRangeStart(dateRange)

  let query = supabase
    .from('analytics_events')
    .select('event_name, event_category, user_id')
    .gte('created_at', start.toISOString())

  if (category && category !== 'all') {
    query = query.eq('event_category', category)
  }

  const { data, error } = await query

  if (error || !data) return []

  const map = new Map<string, { category: string; count: number; users: Set<string> }>()

  for (const row of data as { event_name: string; event_category: string; user_id: string | null }[]) {
    const key = row.event_name
    if (!map.has(key)) {
      map.set(key, { category: row.event_category, count: 0, users: new Set() })
    }
    const entry = map.get(key)!
    entry.count++
    if (row.user_id) entry.users.add(row.user_id)
  }

  return Array.from(map.entries())
    .map(([name, val]) => ({
      event_name: name,
      event_category: val.category,
      count: val.count,
      unique_users: val.users.size,
    }))
    .sort((a, b) => b.count - a.count)
}

export async function getEventTrends(
  eventName: string,
  dateRange: DateRange
): Promise<EventTrendPoint[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []

  const start = getDateRangeStart(dateRange)
  const days = dateRange === 'today' ? 1 : dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90

  const { data, error } = await supabase
    .from('analytics_events')
    .select('created_at')
    .eq('event_name', eventName)
    .gte('created_at', start.toISOString())

  if (error || !data) return []

  const now = new Date()
  const buckets = new Map<string, number>()
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now)
    d.setDate(d.getDate() - i)
    buckets.set(d.toISOString().split('T')[0], 0)
  }

  for (const row of data as { created_at: string }[]) {
    const date = new Date(row.created_at).toISOString().split('T')[0]
    if (buckets.has(date)) {
      buckets.set(date, (buckets.get(date) || 0) + 1)
    }
  }

  return Array.from(buckets.entries()).map(([date, count]) => ({ date, count }))
}

export async function getFunnelData(
  flowName: string,
  dateRange: DateRange
): Promise<FunnelStep[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []

  const start = getDateRangeStart(dateRange)

  const { data, error } = await supabase
    .from('analytics_events')
    .select('flow_step, flow_step_index, user_id')
    .eq('event_category', 'funnel')
    .eq('flow_name', flowName)
    .gte('created_at', start.toISOString())

  if (error || !data) return []

  const map = new Map<string, { index: number; count: number; users: Set<string> }>()

  for (const row of data as { flow_step: string; flow_step_index: number; user_id: string | null }[]) {
    if (!row.flow_step) continue
    if (!map.has(row.flow_step)) {
      map.set(row.flow_step, { index: row.flow_step_index, count: 0, users: new Set() })
    }
    const entry = map.get(row.flow_step)!
    entry.count++
    if (row.user_id) entry.users.add(row.user_id)
  }

  return Array.from(map.entries())
    .map(([step, val]) => ({
      step,
      step_index: val.index,
      count: val.count,
      unique_users: val.users.size,
    }))
    .sort((a, b) => a.step_index - b.step_index)
}

export async function getAvailableFunnels(): Promise<string[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []

  const { data, error } = await supabase
    .from('analytics_events')
    .select('flow_name')
    .eq('event_category', 'funnel')
    .not('flow_name', 'is', null)

  if (error || !data) return []

  const unique = new Set<string>()
  for (const row of data as { flow_name: string }[]) {
    unique.add(row.flow_name)
  }

  return Array.from(unique).sort()
}

export async function getEventCategories(): Promise<string[]> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return []

  const { data, error } = await supabase
    .from('analytics_events')
    .select('event_category')

  if (error || !data) return []

  const unique = new Set<string>()
  for (const row of data as { event_category: string }[]) {
    unique.add(row.event_category)
  }

  return Array.from(unique).sort()
}

// ============================================
// ADMIN PULSE (Motivational Dashboard)
// ============================================

export interface PulseStats {
  totalUsers: number
  totalHabits: number
  totalCompletions: number
  totalConquests: number
  totalBadgesEarned: number
  totalFollows: number
  totalPartnerships: number
  totalStreakDays: number
  totalShardsCirculated: number
  completionsToday: number
  newUsersToday: number
  newUsersThisWeek: number
  premiumUsers: number
  activeUsersToday: number
  longestStreak: number
  avgLevel: number
}

export async function getAdminPulse(): Promise<PulseStats | null> {
  const { isAdmin: adminCheck } = await verifyAdmin()
  if (!adminCheck) return null

  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekStart = new Date(todayStart)
  weekStart.setDate(weekStart.getDate() - 7)

  const [
    profilesResult,
    habitsResult,
    completionsResult,
    conquestsResult,
    badgesResult,
    followsResult,
    partnershipsResult,
    todayCompletionsResult,
    todayUsersResult,
    weekUsersResult,
  ] = await Promise.all([
    supabase.from('user_profiles').select('id, current_streak_global, currency_shards, subscription_tier, level, last_activity_at, created_at'),
    supabase.from('habits').select('id', { count: 'exact', head: true }),
    supabase.from('evidence_logs').select('id, shards_earned', { count: 'exact' })
      .in('ai_verdict', ['approved_gold', 'approved_silver', 'quick_complete']),
    supabase.from('habits').select('id', { count: 'exact', head: true }).not('conquered_at', 'is', null),
    supabase.from('user_badges').select('id', { count: 'exact', head: true }),
    supabase.from('follows').select('id', { count: 'exact', head: true }),
    supabase.from('accountability_partnerships').select('id', { count: 'exact', head: true }).eq('status', 'active'),
    supabase.from('evidence_logs').select('id, user_id', { count: 'exact' })
      .gte('created_at', todayStart.toISOString())
      .in('ai_verdict', ['approved_gold', 'approved_silver', 'quick_complete']),
    supabase.from('user_profiles').select('id', { count: 'exact', head: true })
      .gte('created_at', todayStart.toISOString()),
    supabase.from('user_profiles').select('id', { count: 'exact', head: true })
      .gte('created_at', weekStart.toISOString()),
  ])

  const profiles = (profilesResult.data || []) as {
    id: string
    current_streak_global: number
    currency_shards: number
    subscription_tier: string
    level: number
    last_activity_at: string | null
    created_at: string
  }[]

  const completionLogs = (completionsResult.data || []) as { id: string; shards_earned: number }[]
  const todayCompletionLogs = (todayCompletionsResult.data || []) as { id: string; user_id: string }[]

  const totalStreakDays = profiles.reduce((sum, p) => sum + (p.current_streak_global || 0), 0)
  const totalShardsCirculated = completionLogs.reduce((sum, l) => sum + (l.shards_earned || 0), 0)
  const longestStreak = profiles.reduce((max, p) => Math.max(max, p.current_streak_global || 0), 0)
  const premiumUsers = profiles.filter(p => p.subscription_tier && p.subscription_tier !== 'free').length
  const activeUsersToday = new Set(todayCompletionLogs.map(l => l.user_id)).size
  const avgLevel = profiles.length > 0
    ? Math.round((profiles.reduce((sum, p) => sum + (p.level || 1), 0) / profiles.length) * 10) / 10
    : 1

  return {
    totalUsers: profiles.length,
    totalHabits: habitsResult.count || 0,
    totalCompletions: completionsResult.count || 0,
    totalConquests: conquestsResult.count || 0,
    totalBadgesEarned: badgesResult.count || 0,
    totalFollows: followsResult.count || 0,
    totalPartnerships: partnershipsResult.count || 0,
    totalStreakDays,
    totalShardsCirculated,
    completionsToday: todayCompletionsResult.count || 0,
    newUsersToday: todayUsersResult.count || 0,
    newUsersThisWeek: weekUsersResult.count || 0,
    premiumUsers,
    activeUsersToday,
    longestStreak,
    avgLevel,
  }
}
